import { Linkedin, Loader2, MoreHorizontal } from 'lucide-react';
import React from 'react';
import { cn } from '../lib/utils';

/**
 * ExternalCandidateCard Component
 * Card view for displaying candidate information
 *
 * @param {Object} props
 * @param {Object} props.candidate - Candidate data
 * @param {Function} props.onActionsClick - Actions button handler
 */
export function ExternalCandidateCard({ candidate, onActionsClick }) {
  const isProcessing = candidate.status === 'processing';

  // Get LinkedIn URL (check both possible field names)
  const linkedinUrl = candidate.linkedin_url || candidate.linkedinUrl || candidate.contact_info?.linkedin;

  const photoUrl = candidate.photo_url || candidate.photoUrl || linkedinUrl
    ? `https://avatar.vercel.sh/${encodeURIComponent(candidate.name || 'user')}`
    : null;

  // Get top 3 skills
  const topSkills = candidate.skills?.slice(0, 3) || [];
  const remainingSkills = candidate.skills?.length > 3 ? candidate.skills.length - 3 : 0;

  // Format location (handle object or string)
  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    if (typeof location === 'string') return location;
    if (typeof location === 'object') {
      const { city, country } = location;
      if (city && country) return `${city}, ${country}`;
      if (city) return city;
      if (country) return country;
    }
    return 'Location not specified';
  };

  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-lg transition p-6',
        isProcessing && 'opacity-75'
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        {photoUrl && (
          <img
            src={photoUrl}
            alt={candidate.name}
            className="w-16 h-16 rounded-full object-cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {candidate.name || 'Unknown'}
          </h3>
          <p className="text-sm text-gray-600 truncate">
            {candidate.title || candidate.headline || 'Title not available'}
          </p>
          <p className="text-xs text-gray-500">
            {formatLocation(candidate.location)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold text-blue-600">
            {isProcessing ? '—' : `${candidate.match_percentage || 0}%`}
          </div>
          <div className="text-xs text-gray-500">Match</div>
        </div>
      </div>

      {/* Company */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium">Company:</span>{' '}
          {candidate.organization_name || candidate.company || 'Company not specified'}
        </p>
      </div>

      {/* Skills */}
      <div className="mb-4">
        <p className="text-xs font-medium text-gray-700 mb-2">Skills</p>
        {isProcessing ? (
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
            <span className="text-sm text-gray-500">Processing...</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {topSkills.map((skill, idx) => (
              <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                {skill}
              </span>
            ))}
            {remainingSkills > 0 && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                +{remainingSkills} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Status Badge */}
      <div className="mb-4">
        {isProcessing ? (
          <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Processing
          </span>
        ) : (
          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
            Processed
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          className="flex-1 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition flex items-center justify-center gap-2 font-medium disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          onClick={() => onActionsClick && onActionsClick(candidate)}
          disabled={isProcessing}
        >
          <MoreHorizontal className="w-4 h-4" />
          {isProcessing ? 'Processing...' : 'Actions'}
        </button>
        {linkedinUrl && (
          <button
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => window.open(linkedinUrl, '_blank')}
            disabled={isProcessing}
          >
            <Linkedin className="w-4 h-4 text-blue-600" />
          </button>
        )}
      </div>
    </div>
  );
}



